import { useState, useEffect, useCallback, useMemo } from 'react'
import { BarChart3, Users, DollarSign, Calendar, Activity } from 'lucide-react'
import { SkeletonPage } from '@/components/ui/Skeleton'
import { DateRangeFilter, type DateRange } from '@/components/DateRangeFilter'

interface SeasonMetrics {
  revenue_total: number
  guests: number
  total_scans: number
  paid_scans: number
  total_hours: number
  avg_revenue_per_event: number
  avg_guests_per_event: number
}

interface EventRow {
  event_id: number
  event_name: string
  start_date: string
  status: string
  revenue: number
  guests: number
  total_scans: number
  hours_open: number
}

interface AttractionRow {
  rank: number
  attraction_name: string
  attraction_type: string
  total_scans: number
  unique_guests: number
  revenue: number
  events_appeared: number
}

interface PlanRow {
  rank: number
  plan_name: string
  units_sold: number
  revenue: number
  events_appeared: number
}

interface SeasonData {
  source: string
  scope: string
  date_from: string
  date_to: string
  events_total: number
  events_with_data: number
  metrics: SeasonMetrics
  per_event: EventRow[]
  top_attractions: AttractionRow[]
  top_plans: PlanRow[]
  generated_at: string
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toLocaleString()}`
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

const currentYear = new Date().getFullYear().toString()

export function SeasonPage() {
  const [dateRange, setDateRange] = useState<DateRange>({
    preset: 'ytd',
    year: currentYear,
    date_from: `${currentYear}-01-01`,
    date_to: new Date().toISOString().slice(0, 10),
  })

  const [data, setData] = useState<SeasonData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSeason = useCallback(async (range: DateRange) => {
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    if (range.year) params.set('year', range.year)
    if (range.date_from) params.set('date_from', range.date_from)
    if (range.date_to) params.set('date_to', range.date_to)
    if (range.hour_from != null) params.set('hour_from', String(range.hour_from))
    if (range.hour_to != null) params.set('hour_to', String(range.hour_to))

    try {
      const res = await fetch(`/api/dashboard/season?${params}`, { credentials: 'same-origin' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const payload = await res.json() as SeasonData
      setData(payload)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => fetchSeason(dateRange), 400)
    return () => clearTimeout(timer)
  }, [dateRange, fetchSeason])

  const maxEventRev = useMemo(() => {
    if (!data?.per_event.length) return 1
    return Math.max(...data.per_event.map(e => e.revenue), 1)
  }, [data])

  const maxAttrRev = useMemo(() => {
    if (!data?.top_attractions.length) return 1
    return Math.max(...data.top_attractions.map(a => a.revenue), 1)
  }, [data])

  const m = data?.metrics

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
          <BarChart3 className="h-5 w-5 text-butler-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl text-ink-dark">All Events</h1>
          <p className="text-sm text-ink-soft">Season and year-to-date performance across all events</p>
        </div>
      </div>

      <DateRangeFilter value={dateRange} onChange={setDateRange} />

      {loading && <SkeletonPage />}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load season data: {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* KPI Hero */}
          <div className="mm-card-v3 p-6">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-ink-soft">Total Season Revenue</p>
                <h2 className="font-display font-bold text-3xl text-ink-dark mt-1">
                  {m ? fmt(m.revenue_total) : '—'}
                </h2>
              </div>
              <div className="text-right text-xs text-ink-soft">
                <div>{data.date_from} → {data.date_to}</div>
                <div>{data.events_with_data} events with data</div>
              </div>
            </div>
          </div>

          {/* 4 KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon={<Users className="h-4 w-4" />}
              label="Total Guests"
              value={m ? fmtNum(m.guests) : '—'}
            />
            <KpiCard
              icon={<DollarSign className="h-4 w-4" />}
              label="Avg Revenue / Event"
              value={m ? fmt(m.avg_revenue_per_event) : '—'}
            />
            <KpiCard
              icon={<Calendar className="h-4 w-4" />}
              label="Events"
              value={`${data.events_with_data} / ${data.events_total}`}
            />
            <KpiCard
              icon={<Activity className="h-4 w-4" />}
              label="Avg Guests / Event"
              value={m ? fmtNum(m.avg_guests_per_event) : '—'}
            />
          </div>

          {/* Revenue by Event */}
          <section className="mm-card-v3 p-5">
            <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Revenue by Event</h2>
            {data.per_event.length === 0 ? (
              <div className="py-6 text-center text-xs text-ink-soft">No events in this range.</div>
            ) : (
              <div className="space-y-2.5">
                {data.per_event.map(ev => (
                  <div key={ev.event_id} className="flex items-center gap-3">
                    <span className="w-40 text-xs text-ink-dark shrink-0 truncate font-medium" title={ev.event_name}>
                      {ev.event_name}
                    </span>
                    <span className="w-16 text-[10px] text-ink-soft shrink-0">
                      {ev.start_date?.slice(5)}
                    </span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.round((ev.revenue / maxEventRev) * 100)}%`,
                          background: 'linear-gradient(90deg, #0070ab, #00c7ca)',
                        }}
                      />
                    </div>
                    <span className="mm-data text-xs font-medium text-ink-dark w-16 text-right">
                      {fmt(ev.revenue)}
                    </span>
                    <span className="mm-data text-[10px] text-ink-soft w-16 text-right">
                      {fmtNum(ev.guests)} guests
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Top Attractions + Top Plans side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">
                Top Attractions (Season)
              </h2>
              <div className="space-y-2.5">
                {data.top_attractions.slice(0, 10).map(a => (
                  <div key={a.attraction_name} className="flex items-center gap-3">
                    <span className="w-28 text-xs text-ink-soft shrink-0 truncate" title={a.attraction_name}>
                      {a.attraction_name}
                    </span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.round((a.revenue / maxAttrRev) * 100)}%`,
                          background: 'linear-gradient(90deg, #0070ab, #00c7ca)',
                        }}
                      />
                    </div>
                    <span className="mm-data text-xs font-medium text-ink-dark w-16 text-right">
                      {fmt(a.revenue)}
                    </span>
                    <span className="text-[10px] text-ink-soft w-10 text-right">
                      {a.events_appeared}ev
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">
                Top Plans (Season)
              </h2>
              <div className="space-y-2.5">
                {data.top_plans.map(p => {
                  const maxPlanRev = data.top_plans[0]?.revenue || 1
                  return (
                    <div key={p.plan_name} className="flex items-center gap-3">
                      <span className="w-28 text-xs text-ink-soft shrink-0 truncate" title={p.plan_name}>
                        {p.plan_name}
                      </span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${Math.round((p.revenue / maxPlanRev) * 100)}%`,
                            background: 'linear-gradient(90deg, #0070ab, #00c7ca)',
                          }}
                        />
                      </div>
                      <span className="mm-data text-xs font-medium text-ink-dark w-16 text-right">
                        {fmt(p.revenue)}
                      </span>
                      <span className="text-[10px] text-ink-soft w-12 text-right">
                        {fmtNum(p.units_sold)} sold
                      </span>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  )
}

function KpiCard({ icon, label, value }: {
  icon: React.ReactNode; label: string; value: string
}) {
  return (
    <div className="mm-card-v3 p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-butler-600">{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wide text-ink-soft">{label}</span>
      </div>
      <div className="font-display font-bold text-xl text-ink-dark">{value}</div>
    </div>
  )
}
